/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useSipProfileList.ts
 🗓️ Created: 2025-01-07
 🧩 Description:
    Custom hook for managing SIP Profiles list.
    Integrates with useDataGrid for pagination, sorting, and search.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { fetchSipProfiles } from '../api';
import { SipProfile } from '../types';

/**
 * Custom React hook for managing the SIP Profiles DataGrid list.
 * Integrates directly with `useDataGrid` for pagination, sorting, and search capabilities.
 * 
 * @param initialColumns - Optional array of DataGrid columns to initialize the list with.
 * @returns The DataGrid state and methods for managing the SIP Profiles list.
 */
export function useSipProfileList(initialColumns?: DataGridColumn<SipProfile>[]) {
  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchSipProfiles(params);
        return {
          data: result.data || [],
          pagination: result.pagination || {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
          },
        };
      } catch (error) {
        return {
          data: [] as SipProfile[],
          pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
        };
      }
    },
    [],
  );

  return useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'sip-profiles',
  });
}

